import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../lib/AuthContext";
import { useSubjects } from "../lib/SubjectsContext";
import { useI18n } from "../lib/LanguageContext";
import { api, API } from "../lib/api";
import { Navigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { toast } from "sonner";
import { Camera, Mic, Search, Trash2, Sparkles, Video, User, CalendarIcon, History, FileText, Files, GitCompare, ClipboardList, Printer } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import SubjectSwitcher from "../components/SubjectSwitcher";
import { MacroPie, NutrientRadar } from "../components/NutrientCharts";
import WarningAlerts from "../components/WarningAlerts";
import CustomNutrientsCard from "../components/CustomNutrientsCard";
import { localizeContent } from "../lib/translations";

const STANDARDS = [
  { v: "male_adult", l: "Pria Dewasa (AKG Indonesia)" },
  { v: "female_adult", l: "Wanita Dewasa (AKG Indonesia)" },
  { v: "male_teen", l: "Pria Remaja" },
  { v: "female_teen", l: "Wanita Remaja" },
];

export const NUTRIENTS = [
  // macros
  { k: "kcal", l: "Energi", u: "kcal", g: "macro" },
  { k: "protein", l: "Protein", u: "g", g: "macro" },
  { k: "fat", l: "Lemak Total", u: "g", g: "macro" },
  { k: "sat_fat", l: "Lemak Jenuh", u: "g", g: "macro" },
  { k: "cholesterol", l: "Kolesterol", u: "mg", g: "macro" },
  { k: "carb", l: "Karbohidrat", u: "g", g: "macro" },
  { k: "sugar", l: "Gula", u: "g", g: "macro" },
  { k: "fiber", l: "Serat", u: "g", g: "macro" },
  // vitamins
  { k: "vit_a", l: "Vitamin A", u: "µg", g: "vitamin" },
  { k: "vit_d", l: "Vitamin D", u: "µg", g: "vitamin" },
  { k: "vit_e", l: "Vitamin E", u: "mg", g: "vitamin" },
  { k: "vit_k", l: "Vitamin K", u: "µg", g: "vitamin" },
  { k: "vit_c", l: "Vitamin C", u: "mg", g: "vitamin" },
  { k: "vit_b1", l: "Tiamin (B1)", u: "mg", g: "vitamin" },
  { k: "vit_b2", l: "Riboflavin (B2)", u: "mg", g: "vitamin" },
  { k: "vit_b3", l: "Niasin (B3)", u: "mg", g: "vitamin" },
  { k: "vit_b5", l: "As. Pantotenat (B5)", u: "mg", g: "vitamin" },
  { k: "vit_b6", l: "Piridoksin (B6)", u: "mg", g: "vitamin" },
  { k: "vit_b7", l: "Biotin (B7)", u: "µg", g: "vitamin" },
  { k: "vit_b9", l: "Folat (B9)", u: "µg", g: "vitamin" },
  { k: "vit_b12", l: "Kobalamin (B12)", u: "µg", g: "vitamin" },
  // minerals
  { k: "calcium", l: "Kalsium", u: "mg", g: "mineral" },
  { k: "iron", l: "Zat Besi", u: "mg", g: "mineral" },
  { k: "magnesium", l: "Magnesium", u: "mg", g: "mineral" },
  { k: "phosphorus", l: "Fosfor", u: "mg", g: "mineral" },
  { k: "potassium", l: "Kalium", u: "mg", g: "mineral" },
  { k: "sodium", l: "Natrium", u: "mg", g: "mineral" },
  { k: "zinc", l: "Seng", u: "mg", g: "mineral" },
  { k: "copper", l: "Tembaga", u: "µg", g: "mineral" },
  { k: "manganese", l: "Mangan", u: "mg", g: "mineral" },
  { k: "selenium", l: "Selenium", u: "µg", g: "mineral" },
  { k: "iodine", l: "Iodium", u: "µg", g: "mineral" },
];

function ageFromDob(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr); if (isNaN(dob)) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age < 0 || age > 130 ? null : age;
}

export default function NunaDashboard() {
  const { user, refresh, loading } = useAuth();
  const { active: activeSubject } = useSubjects();
  const { lang } = useI18n();
  const tr = (value) => localizeContent(value, lang);

  const [subject, setSubject] = useState({ name: "", dob: null, age: 25, weight: 60, height: 165, sex: "male", activity: "sedang" });
  const [standard, setStandard] = useState("male_adult");
  const [entries, setEntries] = useState([]);
  const [query, setQuery] = useState(""); const [foods, setFoods] = useState([]); const [source, setSource] = useState("");
  const [sources, setSources] = useState({ sources: [], categories: [] });
  const [result, setResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [beforeFile, setBeforeFile] = useState(null); const [afterFile, setAfterFile] = useState(null);
  const [history, setHistoryData] = useState([]); const [historyOpen, setHistoryOpen] = useState(false);
  const [customValues, setCustomValues] = useState({});

  useEffect(() => { if (user && !subject.name) setSubject((s)=>({...s, name: user.full_name})); }, [user]); // eslint-disable-line
  useEffect(() => {
    if (activeSubject) {
      setSubject({
        name: activeSubject.name || "", dob: activeSubject.dob || null,
        age: activeSubject.age || 25, sex: activeSubject.sex || "male",
        weight: activeSubject.weight || 60, height: activeSubject.height || 165,
        activity: activeSubject.activity || "sedang",
      });
      setStandard(activeSubject.sex === "female" ? "female_adult" : "male_adult");
    }
  }, [activeSubject]);
  useEffect(() => { api.get("/foods/sources").then(r => setSources(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams(); if (query) params.set("q", query); if (source) params.set("source", source);
      api.get(`/foods?${params}`).then(r => setFoods(r.data));
    }, 250); return () => clearTimeout(t);
  }, [query, source]);

  const setDob = (date) => {
    const iso = date ? date.toISOString().slice(0,10) : null;
    const age = ageFromDob(iso);
    setSubject((s) => ({ ...s, dob: iso, age: age ?? s.age }));
  };

  const getFoodName = (food) => {
  if (!food) return "";

  if (lang === "ko") {
    return food.name_ko || food.name_en || food.name || "-";
  }

  if (lang === "en") {
    return food.name_en || food.name || food.name_ko || "-";
  }

  return food.name || food.name_en || food.name_ko || "-";
};

const addFood = (f) => {
  setEntries(prev => [...prev, {
    food_id: f.id,
    name: f.name,
    name_en: f.name_en,
    name_ko: f.name_ko,
    grams: 100,
    kcal: f.kcal,
    protein: f.protein,
    fat: f.fat,
    carb: f.carb,
    fiber: f.fiber,
    calcium: f.calcium,
    iron: f.iron,
    vit_a: f.vit_a,
    vit_c: f.vit_c,
    sodium: f.sodium,
    _base: f
  }]);

  toast.success(`${getFoodName(f)} ditambahkan`);
};

  const updateGrams = (i, g) => setEntries(prev => prev.map((e, idx) => idx===i ? { ...e, grams: Number(g) } : e));
  const removeEntry = (i) => setEntries(prev => prev.filter((_,idx)=>idx!==i));

  const totals = useMemo(() => {
    const t = {}; NUTRIENTS.forEach(n => t[n.k] = 0);
    entries.forEach(e => {
      const factor = (e.grams || 0) / 100;
      NUTRIENTS.forEach(n => { const base = e._base ? e._base[n.k] : e[n.k]; t[n.k] += (Number(base) || 0) * factor; });
    });
    return t;
  }, [entries]);

  const computeAnalysis = async () => {
    if (entries.length === 0) return toast.error(tr("Tambahkan minimal 1 makanan"));
    try {
      const payload = { title: `Analisis ${new Date().toLocaleDateString("id-ID")}`, subject, standard,
        entries: entries.map(e => ({ food_id: e.food_id, name: e.name, grams: e.grams,
          kcal: e._base?.kcal||0, protein: e._base?.protein||0, fat: e._base?.fat||0, carb: e._base?.carb||0,
          fiber: e._base?.fiber||0, calcium: e._base?.calcium||0, iron: e._base?.iron||0,
          vit_a: e._base?.vit_a||0, vit_c: e._base?.vit_c||0, sodium: e._base?.sodium||0 })),
        custom_values: Object.keys(customValues).length ? customValues : undefined };
      const r = await api.post("/nuna/compute", payload);
      setResult(r.data); await refresh();
      toast.success(tr("Analisis berhasil"));
    } catch (err) { toast.error(err.response?.data?.detail || tr("Gagal analisis")); }
  };

  const aiUpload = async (file, mode) => {
    setAiLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("mode", mode);
      const r = await api.post("/nuna/ai-analyze", fd, { headers: { "Content-Type": "multipart/form-data" }});
      const items = r.data.items || [];
      if (items.length === 0) { toast.error(tr("AI tidak mengenali makanan. Coba lagi.")); }
      else {
        const newEntries = items.map(it => ({ food_id: null, name: it.name || it.name_en || "Item", grams: it.grams || 100,
          _base: { kcal: it.kcal, protein: it.protein, fat: it.fat, carb: it.carb, fiber: it.fiber,
                   calcium: it.calcium, iron: it.iron, vit_a: it.vit_a, vit_c: it.vit_c, sodium: it.sodium } }));
        setEntries(prev => [...prev, ...newEntries]);
        toast.success(`${tr("AI mengidentifikasi")} ${items.length} ${tr("item")}`);
      }
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "AI gagal"); }
    finally { setAiLoading(false); }
  };

  const compareBeforeAfter = async () => {
      if (!beforeFile || !afterFile) return toast.error(tr("Upload kedua foto (sebelum & sesudah)"));
    setAiLoading(true);
    try {
      const fd = new FormData(); fd.append("before", beforeFile); fd.append("after", afterFile);
      const r = await api.post("/nuna/before-after", fd, { headers: { "Content-Type": "multipart/form-data" }});
      const items = r.data.items || [];
      if (items.length === 0) return toast.error(tr("AI tidak dapat membandingkan"));
      const newEntries = items.filter(it => (it.grams_consumed||0) > 0).map(it => ({
        food_id: null, name: `${it.name || "Item"} (dikonsumsi)`, grams: it.grams_consumed,
        _base: { kcal: it.kcal||0, protein: it.protein||0, fat: it.fat||0, carb: it.carb||0, fiber: it.fiber||0,
                 calcium: it.calcium||0, iron: it.iron||0, vit_a: it.vit_a||0, vit_c: it.vit_c||0, sodium: it.sodium||0 }
      }));
      setEntries(prev => [...prev, ...newEntries]);
      toast.success(`${tr("Dibandingkan")} ${items.length} ${tr("item")}, ${newEntries.length} ${tr("dikonsumsi")}`);
      setBeforeFile(null); setAfterFile(null);
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal membandingkan"); }
    finally { setAiLoading(false); }
  };

  const downloadPDF = () => {
    if (!result) return toast.error(tr("Hitung analisis dulu"));
    try {
      const doc = new jsPDF();
      doc.setFontSize(20); doc.setTextColor(47,163,159); doc.text("GIDZA INSTITUTE — NUNA Report", 14, 20);
      doc.setFontSize(10); doc.setTextColor(80);
      doc.text(`Nama: ${result.subject.name || "-"}`, 14, 32);
      doc.text(`Usia: ${result.subject.age} th   BB: ${result.subject.weight} kg   TB: ${result.subject.height} cm`, 14, 38);
      doc.text(`BMI: ${result.bmi ?? "-"}   Aktivitas: ${result.subject.activity}`, 14, 44);
      doc.text(`Standar: ${result.standard}   Tanggal: ${new Date(result.created_at).toLocaleString("id-ID")}`, 14, 50);
      autoTable(doc, { startY: 58, head: [["Makanan","Berat (g)","kcal","Protein","Lemak","Karbo"]],
        body: result.entries.map(e => [e.name, e.grams, Number(e.kcal).toFixed(1), Number(e.protein).toFixed(1), Number(e.fat).toFixed(1), Number(e.carb).toFixed(1)]) });
      const y = (doc.lastAutoTable?.finalY || 100) + 8;
      autoTable(doc, { startY: y, head: [["Nutrient","Total","AKG","%"]],
        body: NUTRIENTS.map(n => [n.l, `${(result.totals[n.k]||0).toFixed(2)} ${n.u}`, `${result.standard_values[n.k]} ${n.u}`, `${result.percentages[n.k]||0}%`]) });
      doc.save(`NUNA_${result.subject.name || "report"}.pdf`);
      toast.success(tr("PDF berhasil diunduh"));
    } catch (err) { console.error(err); toast.error(tr("Gagal membuat PDF")); }
  };

  const downloadDocx = async () => {
    if (!result) return toast.error(tr("Hitung analisis dulu"));
    try {
      const token = localStorage.getItem("gidza_token");
      const resp = await fetch(`${API}/nuna/docx/${result.id}`, { headers: { Authorization: `Bearer ${token}` }});
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = `NUNA_${result.subject.name || "report"}.docx`; a.click(); URL.revokeObjectURL(url);
      toast.success(tr("Word berhasil diunduh"));
    } catch { toast.error(tr("Gagal membuat Word")); }
  };

  const openHistory = async () => {
    try { const r = await api.get("/nuna/history"); setHistoryData(r.data); setHistoryOpen(true); }
    catch { toast.error(tr("Gagal memuat riwayat")); }
  };

  const restoreAnalysis = (h) => {
    setResult(h); setSubject(h.subject); setStandard(h.standard);
    setEntries(h.entries.map(e => ({ food_id: e.food_id, name: e.name, grams: e.grams,
      _base: { kcal: e.kcal*100/(e.grams||1), protein: e.protein*100/(e.grams||1),
               fat: e.fat*100/(e.grams||1), carb: e.carb*100/(e.grams||1),
               fiber: (e.fiber||0)*100/(e.grams||1), calcium: (e.calcium||0)*100/(e.grams||1),
               iron: (e.iron||0)*100/(e.grams||1), vit_a: (e.vit_a||0)*100/(e.grams||1),
               vit_c: (e.vit_c||0)*100/(e.grams||1), sodium: (e.sodium||0)*100/(e.grams||1) } })));
    setHistoryOpen(false); toast.success(tr("Analisis dimuat"));
  };

  const deleteHistoryItem = async (id) => {
    if (!window.confirm(tr("Hapus analisis ini?"))) return;
    try { await api.delete(`/nuna/history/${id}`); setHistoryData(h => h.filter(x => x.id !== id)); toast.success(tr("Terhapus")); }
    catch { toast.error(tr("Gagal menghapus")); }
  };

  const lim = user?.tier === "free";
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-teal border-t-transparent rounded-full animate-spin"/></div>;
  if (!user) return <Navigate to="/masuk" replace/>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8" data-testid="nuna-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-teal mb-1">NUNA</div>
          <h1 className="font-display text-3xl md:text-4xl font-medium">{tr("Nutrition Analysis Workspace")}</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <SubjectSwitcher/>
          <div className="text-xs bg-teal-soft text-teal font-semibold px-3 py-2 rounded-md">
            {tr("Tier")}: <span className="uppercase">{user.tier}</span> {lim && <>· {tr("Manual")} {user.manual_uses}/7 · AI {user.ai_uses}/3</>}
          </div>
          <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
            <SheetTrigger asChild><Button size="sm" variant="outline" className="rounded-md" onClick={openHistory} data-testid="history-btn"><History className="w-4 h-4 mr-1"/> {tr("Riwayat")}</Button></SheetTrigger>
            <SheetContent className="bg-white w-[400px] sm:w-[500px] overflow-y-auto">
              <SheetHeader><SheetTitle className="font-display">{tr("Riwayat Analisis")}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-2" data-testid="history-list">
                {history.length===0 ? <p className="text-sm text-muted-foreground">{tr("Belum ada riwayat.")}</p> :
                  history.map(h => (
                    <div key={h.id} className="border border-teal/10 rounded-md p-3" data-testid={`history-item-${h.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{h.title}</div>
                          <div className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleString("id-ID")} · {h.entries.length} item · {h.totals.kcal.toFixed(0)} kcal · BMI {h.bmi ?? "-"}</div>
                        </div>
                        <button onClick={()=>deleteHistoryItem(h.id)} className="text-destructive p-1 shrink-0"><Trash2 className="w-4 h-4"/></button>
                      </div>
                      <Button size="sm" variant="outline" className="mt-2 rounded-md text-xs h-7" onClick={()=>restoreAnalysis(h)} data-testid={`restore-${h.id}`}>{tr("Muat ulang")}</Button>
                    </div>
                  ))
                }
              </div>
            </SheetContent>
          </Sheet>
          <Link to="/ffq"><Button size="sm" variant="outline" className="rounded-md" data-testid="ffq-link"><ClipboardList className="w-4 h-4 mr-1"/> FFQ</Button></Link>
          {lim && <Link to="/harga"><Button size="sm" className="rounded-md bg-teal hover:bg-teal/90 text-white">{tr("Upgrade")}</Button></Link>}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* LEFT */}
        <div className="xl:col-span-4 space-y-4">
          <Card className="dash-panel p-5" data-testid="subject-panel">
            <div className="flex items-center gap-2 mb-4"><User className="w-4 h-4 text-teal"/><h3 className="font-display font-medium">{tr("Data Subjek")}</h3></div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr("Nama")}><Input value={subject.name} onChange={(e)=>setSubject({...subject, name:e.target.value})}/></Field>
              <Field label={tr("Tgl Lahir")}>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start h-10 text-left font-normal" data-testid="dob-picker-btn">
                      <CalendarIcon className="w-4 h-4 mr-2 text-teal"/>
                      {subject.dob ? format(new Date(subject.dob), "dd MMM yyyy") : <span className="text-muted-foreground">{tr("Pilih tanggal")}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <Calendar mode="single" selected={subject.dob ? new Date(subject.dob) : undefined} onSelect={setDob}
                      captionLayout="dropdown-buttons" fromYear={1930} toYear={new Date().getFullYear()} initialFocus/>
                  </PopoverContent>
                </Popover>
              </Field>
              <Field label={tr("Usia (auto)")}><Input type="number" value={subject.age} onChange={(e)=>setSubject({...subject, age:+e.target.value})} data-testid="age-input"/></Field>
              <Field label={tr("Jenis")}><select value={subject.sex} onChange={(e)=>setSubject({...subject, sex:e.target.value})} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-white"><option value="male">{tr("Pria")}</option><option value="female">{tr("Wanita")}</option></select></Field>
              <Field label={tr("BB (kg)")}><Input type="number" value={subject.weight} onChange={(e)=>setSubject({...subject, weight:+e.target.value})}/></Field>
              <Field label={tr("TB (cm)")}><Input type="number" value={subject.height} onChange={(e)=>setSubject({...subject, height:+e.target.value})}/></Field>
              <Field label={tr("Aktivitas")}><select value={subject.activity} onChange={(e)=>setSubject({...subject, activity:e.target.value})} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-white"><option value="ringan">{tr("ringan")}</option><option value="sedang">{tr("sedang")}</option><option value="berat">{tr("berat")}</option></select></Field>
              <Field label={tr("Standar AKG")}><select value={standard} onChange={(e)=>setStandard(e.target.value)} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-white">{STANDARDS.map(s=><option key={s.v} value={s.v}>{tr(s.l)}</option>)}</select></Field>
            </div>
          </Card>

          <Card className="dash-panel p-5" data-testid="ai-panel">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-teal"/><h3 className="font-display font-medium">{tr("Input AI (Foto/Video/Suara)")}</h3></div>
            <p className="text-xs text-muted-foreground mb-3">Gemini 3.1 Pro — {tr("identifikasi & estimasi gram.")}</p>
            <div className="grid grid-cols-3 gap-2">
              <UploadBtn accept="image/*" onFile={(f)=>aiUpload(f,"image")} label={tr("Foto")} icon={<Camera className="w-4 h-4"/>} disabled={aiLoading} testId="upload-image"/>
              <UploadBtn accept="video/*" onFile={(f)=>aiUpload(f,"video")} label={tr("Video")} icon={<Video className="w-4 h-4"/>} disabled={aiLoading} testId="upload-video"/>
              <UploadBtn accept="audio/*" onFile={(f)=>aiUpload(f,"audio")} label={tr("Suara")} icon={<Mic className="w-4 h-4"/>} disabled={aiLoading} testId="upload-audio"/>
            </div>
          </Card>

          <Card className="dash-panel p-5" data-testid="before-after-panel">
            <div className="flex items-center gap-2 mb-2"><GitCompare className="w-4 h-4 text-teal"/><h3 className="font-display font-medium">{tr("Bandingkan Sebelum & Sesudah")}</h3></div>
            <p className="text-xs text-muted-foreground mb-3">{tr("Estimasi berat gram yang benar-benar dikonsumsi.")}</p>
            <div className="grid grid-cols-2 gap-3">
              <FilePicker label={tr("Sebelum")} file={beforeFile} onFile={setBeforeFile} testId="before-file"/>
              <FilePicker label={tr("Sesudah")} file={afterFile} onFile={setAfterFile} testId="after-file"/>
            </div>
            <Button onClick={compareBeforeAfter} disabled={!beforeFile || !afterFile || aiLoading} className="w-full mt-3 rounded-md bg-teal hover:bg-teal/90 text-white h-9" data-testid="compare-btn">{tr("Bandingkan")}</Button>
            {aiLoading && <div className="mt-2 text-xs text-teal">{tr("AI sedang bekerja...")}</div>}
          </Card>

          <Card className="dash-panel p-5" data-testid="food-search-panel">
            <div className="flex items-center gap-2 mb-3"><Search className="w-4 h-4 text-teal"/><h3 className="font-display font-medium">{tr("Cari Database Makanan")}</h3></div>
            <Input placeholder={tr("Cari nasi, kimchi, apple...")} value={query} onChange={(e)=>setQuery(e.target.value)} data-testid="food-search-input"/>
            <select value={source} onChange={(e)=>setSource(e.target.value)} className="mt-2 w-full border border-input rounded-md h-10 px-3 text-sm bg-white">
              <option value="">{tr("Semua sumber")}</option>
              {sources.sources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="mt-3 max-h-64 overflow-y-auto no-scrollbar divide-y divide-teal/10">
              {foods.map(f => (
                <button key={f.id} onClick={()=>addFood(f)} className="w-full text-left py-2 hover:bg-teal-soft/40 px-2 rounded" data-testid={`food-item-${f.id}`}>
                  <div className="text-sm font-medium">{getFoodName(f)}</div>
                  <div className="text-xs text-muted-foreground">{f.source} · {tr(f.category)} · {f.kcal} kcal/100g</div>
                </button>
              ))}
            </div>
          </Card>

          <CustomNutrientsCard values={customValues} onValuesChange={setCustomValues}/>
        </div>

        {/* CENTER */}
        <div className="xl:col-span-5 space-y-4">
          <Card className="dash-panel p-5" data-testid="entries-panel">
            <h3 className="font-display font-medium mb-3">{tr("Item Makanan Terpilih")}</h3>
            {entries.length===0 ? <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-teal/20 rounded">{tr("Belum ada item. Cari makanan, upload foto, atau bandingkan sebelum/sesudah.")}</div> :
              <div className="space-y-2">
                {entries.map((e,i) => (
                  <div key={i} className="flex items-center gap-3 border border-teal/10 rounded-md p-2" data-testid={`entry-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {getFoodName(e._base || e)}
                      </div>
                      <div className="text-xs text-muted-foreground">{Math.round((e._base?.kcal||0)*e.grams/100)} kcal</div>
                    </div>
                    <Input type="number" className="w-20 h-9" value={e.grams} onChange={(ev)=>updateGrams(i, ev.target.value)}/>
                    <span className="text-xs">g</span>
                    <button onClick={()=>removeEntry(i)} className="text-destructive p-1"><Trash2 className="w-4 h-4"/></button>
                  </div>
                ))}
              </div>
            }
            <div className="flex gap-2 mt-4 flex-wrap">
              <Button onClick={computeAnalysis} className="rounded-md bg-teal hover:bg-teal/90 text-white" data-testid="compute-btn">{tr("Hitung & Simpan Analisis")}</Button>
              {result && <>
                <Button variant="outline" onClick={downloadPDF} className="rounded-md" data-testid="download-pdf-btn"><FileText className="w-4 h-4 mr-1"/> PDF</Button>
                <Button variant="outline" onClick={downloadDocx} className="rounded-md" data-testid="download-docx-btn"><Files className="w-4 h-4 mr-1"/> Word</Button>
                <Link to={`/nuna/print/${result.id}`}><Button variant="outline" className="rounded-md" data-testid="print-link-btn"><Printer className="w-4 h-4 mr-1"/> {tr("Cetak")}</Button></Link>
              </>}
            </div>
          </Card>

          <Card className="dash-panel p-5" data-testid="totals-panel">
            <h3 className="font-display font-medium mb-3">{tr("Total Sementara")}</h3>
            <Tabs defaultValue="macro">
              <TabsList className="bg-teal-soft">
                <TabsTrigger value="macro" data-testid="tab-macro">{tr("Makro")}</TabsTrigger>
                <TabsTrigger value="vitamin" data-testid="tab-vitamin">{tr("Vitamin")}</TabsTrigger>
                <TabsTrigger value="mineral" data-testid="tab-mineral">{tr("Mineral")}</TabsTrigger>
              </TabsList>
              {["macro","vitamin","mineral"].map(g => (
                <TabsContent key={g} value={g} className="mt-3">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {NUTRIENTS.filter(n => n.g === g).map(n => (
                      <div key={n.k} className="border border-teal/10 rounded-md p-2.5">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{tr(n.l)}</div>
                        <div className="font-display text-base font-medium">{(totals[n.k]||0).toFixed(n.u==="µg"?1:2)}<span className="text-xs text-muted-foreground ml-1">{n.u}</span></div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </Card>
        </div>

        {/* RIGHT */}
        <div className="xl:col-span-3">
          <Card className="dash-panel p-5" data-testid="result-panel">
            <h3 className="font-display font-medium mb-3">{tr("Hasil Analisis")}</h3>
            {!result ? <p className="text-sm text-muted-foreground">{tr('Klik "Hitung & Simpan" untuk melihat hasil dibandingkan AKG.')}</p> :
              <>
                {result.bmi && <div className="mb-4 p-3 bg-teal-soft rounded-md">
                  <div className="text-xs uppercase tracking-wider text-teal">BMI</div>
                  <div className="font-display text-3xl font-medium">{result.bmi}</div>
                  <div className="text-xs text-muted-foreground mt-1">{bmiCategory(result.bmi)}</div>
                </div>}
                <WarningAlerts result={result} onAddFood={addFood}/>
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-widest text-teal font-bold mb-1">{tr("Distribusi Makro")}</div>
                  <MacroPie totals={result.totals}/>
                </div>
                <div className="mb-4">
                  <div className="text-xs uppercase tracking-widest text-teal font-bold mb-1">{tr("Radar % AKG")}</div>
                  <NutrientRadar percentages={result.percentages}/>
                </div>
                <Tabs defaultValue="macro" data-testid="result-tabs">
                  <TabsList className="bg-teal-soft w-full">
                    <TabsTrigger value="macro" className="flex-1" data-testid="result-tab-macro">{tr("Makro")}</TabsTrigger>
                    <TabsTrigger value="vitamin" className="flex-1" data-testid="result-tab-vitamin">{tr("Vitamin")}</TabsTrigger>
                    <TabsTrigger value="mineral" className="flex-1" data-testid="result-tab-mineral">{tr("Mineral")}</TabsTrigger>
                  </TabsList>
                  {["macro","vitamin","mineral"].map(g => (
                    <TabsContent key={g} value={g} className="mt-3 space-y-3">
                      {NUTRIENTS.filter(n => n.g === g).map(n => {
                        const pct = result.percentages[n.k] || 0;
                        return (
                          <div key={n.k} data-testid={`result-${n.k}`}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="font-medium">{n.l}</span>
                              <span className={pct>=80 && pct<=120 ? "text-teal font-semibold" : "text-orange-500 font-semibold"}>{pct}%</span>
                            </div>
                            <div className="nutrient-bar"><span style={{width:`${Math.min(150, pct)}%`}}/></div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">{(result.totals[n.k]||0).toFixed(2)} / {result.standard_values[n.k]} {n.u}</div>
                          </div>
                        );
                      })}
                    </TabsContent>
                  ))}
                </Tabs>
                {result.custom?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-teal/10" data-testid="custom-result">
                    <div className="text-xs uppercase tracking-widest text-teal font-bold mb-2 flex items-center gap-1">Custom Nutrient <span className="text-[9px] bg-amber-50 border border-amber-200 rounded px-1 text-amber-700">Add-on</span></div>
                    <div className="space-y-2">
                      {result.custom.map(c => (
                        <div key={c.key} data-testid={`custom-result-${c.key}`}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-medium">{c.label}</span>
                            <span className={c.pct>=80 && c.pct<=120 ? "text-teal font-semibold" : c.pct>=60 ? "text-orange-500 font-semibold" : "text-red-500 font-semibold"}>{c.pct}%</span>
                          </div>
                          <div className="nutrient-bar"><span style={{width:`${Math.min(150, c.pct)}%`}}/></div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">{c.value.toFixed(2)} / {c.target} {c.unit}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            }
          </Card>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>; }

function UploadBtn({ accept, onFile, label, icon, disabled, testId }) {
  return (
    <label className={`flex flex-col items-center gap-1 p-3 border border-teal/20 rounded-md cursor-pointer hover:bg-teal-soft/40 transition-colors ${disabled?"opacity-50 pointer-events-none":""}`} data-testid={testId}>
      <input type="file" accept={accept} className="hidden" onChange={(e)=>{ if(e.target.files?.[0]) onFile(e.target.files[0]); e.target.value=""; }}/>
      <div className="text-teal">{icon}</div>
      <span className="text-xs">{label}</span>
    </label>
  );
}

function FilePicker({ label, file, onFile, testId }) {
  const { lang } = useI18n();
  return (
    <label className="flex flex-col items-center gap-1 p-3 border border-dashed border-teal/30 rounded-md cursor-pointer hover:bg-teal-soft/40 text-center" data-testid={testId}>
      <input type="file" accept="image/*" className="hidden" onChange={(e)=>{ if(e.target.files?.[0]) onFile(e.target.files[0]); }}/>
      <Camera className="w-4 h-4 text-teal"/>
      <span className="text-xs font-medium">{label}</span>
      <span className="text-[10px] text-muted-foreground truncate max-w-full">{file ? file.name : localizeContent("pilih file", lang)}</span>
    </label>
  );
}

function bmiCategory(b) {
  if (b<18.5) return "Kurus"; if (b<25) return "Normal"; if (b<30) return "Overweight"; return "Obesitas";
}
